import React, { createContext, useContext, useState, useLayoutEffect, useCallback } from 'react';

/**
 * ThemeContext
 * ─────────────
 * Central theme state for the entire app.
 *
 *   theme     : 'editorial' | 'classic' | 'forest' | 'dark' | 'senior-light' | 'high-contrast'
 *   fontSize  : 'small' | 'medium' | 'large' | 'xlarge'
 *
 * The current preference is mirrored onto the <html> element as
 * data-attributes (data-theme, data-font-size) so CSS rules in
 * global.css can react to them via attribute selectors.
 *
 * Important: the attributes are applied at MODULE LOAD time (before any
 * React render) and again synchronously via useLayoutEffect on every
 * change, so the first paint already has the right theme and there is
 * no flash of the default look.
 */

const STORAGE_KEY = 'crp.theme.v2';

export const THEMES = [
  { id: 'editorial',     name: 'Editorial',     desc: 'Warm beige, navy, gold',                     sw1: '#f5f3ef', sw2: '#1a2332' },
  { id: 'classic',       name: 'Classic',       desc: 'Clean white, soft grey, blue',               sw1: '#f7f8fa', sw2: '#2563c8' },
  { id: 'forest',        name: 'Forest',        desc: 'Calming green & cream',                      sw1: '#f4f0e8', sw2: '#5a7a3e' },
  { id: 'dark',          name: 'Dark',          desc: 'Charcoal + amber for low-light use',         sw1: '#1a1d22', sw2: '#d4a843' },
  { id: 'senior-light',  name: 'Senior Light',  desc: 'Warm cream + dark navy — easy reading (default)', sw1: '#fbf6ec', sw2: '#0a4f9c' },
  { id: 'high-contrast', name: 'High Contrast', desc: 'Black on white — max legibility',            sw1: '#ffffff', sw2: '#000000' },
];

export const FONT_SIZES = [
  { id: 'small',  label: 'S',  desc: 'Small'       },
  { id: 'medium', label: 'M',  desc: 'Medium'      },
  { id: 'large',  label: 'L',  desc: 'Large'       }, // default
  { id: 'xlarge', label: 'XL', desc: 'Extra Large' },
];

const THEME_IDS = THEMES.map((t) => t.id);
const FONT_SIZE_IDS = FONT_SIZES.map((f) => f.id);

const DEFAULTS = {
  theme: 'senior-light',
  fontSize: 'large',
};

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return {
      theme: THEME_IDS.includes(parsed.theme) ? parsed.theme : DEFAULTS.theme,
      fontSize: FONT_SIZE_IDS.includes(parsed.fontSize) ? parsed.fontSize : DEFAULTS.fontSize,
    };
  } catch (e) {
    return DEFAULTS;
  }
}

function applyToDom(pref) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', pref.theme);
  root.setAttribute('data-font-size', pref.fontSize);
}

// Apply immediately at module-load time — BEFORE any React render
// happens. This way every component renders against the correct
// CSS variables on its very first paint (no theme flash).
const INITIAL_PREF = readStored();
applyToDom(INITIAL_PREF);

const ThemeContext = createContext({
  ...DEFAULTS,
  setTheme: () => {},
  setFontSize: () => {},
});

export function ThemeProvider({ children }) {
  const [pref, setPref] = useState(INITIAL_PREF);

  // useLayoutEffect fires synchronously after DOM mutation, before paint.
  // Use it instead of useEffect so theme changes are visible immediately
  // without a one-frame delay.
  useLayoutEffect(() => {
    applyToDom(pref);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pref));
    } catch (e) {}
  }, [pref]);

  const setTheme = useCallback(
    (theme) => setPref((p) => ({ ...p, theme })),
    []
  );
  const setFontSize = useCallback(
    (fontSize) => setPref((p) => ({ ...p, fontSize })),
    []
  );

  const value = {
    ...pref,
    setTheme,
    setFontSize,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}